import { useEffect, useRef, useState } from "react";
import type { Coordinate, RouteGeometry } from "../../../shared/strava";

export default function RouteMap({
  route,
  meeting,
  label,
  interactive = false,
}: {
  route?: RouteGeometry | null;
  meeting?: Coordinate | null;
  label: string;
  interactive?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null),
    [visible, setVisible] = useState(false),
    [failed, setFailed] = useState(false);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    if (!globalThis.IntersectionObserver) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const latitude = meeting?.[0],
    longitude = meeting?.[1];
  useEffect(() => {
    const element = root.current;
    if (!visible || !element || (!route && latitude == null)) return;
    let disposed = false,
      map: import("leaflet").Map | undefined,
      resize: ResizeObserver | undefined;
    Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")])
      .then(([L]) => {
        if (disposed) return;
        map = L.map(element, {
          zoomControl: interactive,
          dragging: interactive,
          scrollWheelZoom: interactive,
          doubleClickZoom: interactive,
          touchZoom: interactive,
          boxZoom: interactive,
          keyboard: interactive,
          attributionControl: true,
        });
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);
        const bounds = L.latLngBounds([]);
        if (route) {
          L.polyline(route.coordinates, {
            color: "#275DDE",
            weight: 5,
            opacity: 0.9,
          }).addTo(map);
          route.coordinates.forEach((point) => bounds.extend(point));
          L.circleMarker(route.coordinates[0], {
            radius: 5,
            color: "#275DDE",
            fillColor: "white",
            fillOpacity: 1,
          }).addTo(map);
        }
        if (latitude != null && longitude != null) {
          const point: Coordinate = [latitude, longitude];
          bounds.extend(point);
          L.circleMarker(point, {
            radius: 8,
            color: "white",
            weight: 3,
            fillColor: "#FC5200",
            fillOpacity: 1,
          })
            .bindTooltip("Møtepunkt")
            .addTo(map);
        }
        if (bounds.isValid())
          map.fitBounds(bounds, { padding: [24, 24], maxZoom: 15 });
        resize = new ResizeObserver(() => map?.invalidateSize());
        resize.observe(element);
      })
      .catch(() => {
        if (!disposed) setFailed(true);
      });
    return () => {
      disposed = true;
      resize?.disconnect();
      map?.remove();
    };
  }, [visible, route, latitude, longitude, interactive]);
  return (
    <div
      className={`route-map ${interactive ? "interactive" : ""}`}
      aria-label={label}
      role="region"
    >
      <div ref={root} className="route-map-canvas" />
      {failed && <p>Kartet kunne ikke lastes. Ruten er fortsatt lagret.</p>}
    </div>
  );
}
